import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { fetchCreateCheckoutSession } from 'src/redux/employerUserSignup'
import { fetchTrackEvent, ProductAnalyticsEventTypes } from 'src/redux/productAnalytics'
import { useIdentity } from 'pages/Identity/Login'

export const PLAN_OPTIONS = [
  {
    key: 'professional',
    name: 'Professional',
    price: '$199',
    priceSuffix: '/ month',
    description: null,
    features: [
      'Unlimited job postings',
      'Full candidate access',
      'AI candidate matching',
      'Advanced search filters',
      'Priority support',
    ],
    isHighlighted: true,
    ctaLabel: 'Subscribe',
    ctaVariant: 'contained',
    ctaDisabled: false,
  },
  {
    key: 'hospital',
    name: 'Health Care System',
    price: 'Custom',
    priceSuffix: '',
    description: 'Signing up your whole team? Health care system plans include unlimited users at a lower per-user cost.',
    features: [
      'All Professional plan features, plus:',
      'Unlimited users',
    ],
    isHighlighted: false,
    ctaLabel: 'Contact Us',
    ctaVariant: 'outlined',
    ctaDisabled: false,
  },
]

const DEFAULT_SUBTITLE = 'Upgrade to access candidate profiles, post unlimited jobs, and more.'

const UpgradePlanDialog = ({ open, onClose, subtitle, source }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const theme = useTheme()
  const { email, registrationStatus } = useIdentity()
  const isPendingApproval = registrationStatus === 'Pending'
  const [checkoutLoading, setCheckoutLoading] = useState(false)

  useEffect(() => {
    if (open) {
      dispatch(
        fetchTrackEvent({
          eventName: 'upgrade_dialog_viewed',
          eventType: ProductAnalyticsEventTypes.ACTION,
          distinctId: email,
          eventData: { source },
        }),
      )
    }
  }, [open, dispatch, email, source])

  const handleClose = () => {
    dispatch(
      fetchTrackEvent({
        eventName: 'upgrade_dialog_dismissed',
        eventType: ProductAnalyticsEventTypes.ACTION,
        distinctId: email,
        eventData: { source },
      }),
    )
    onClose()
  }

  const handleContactUs = () => {
    navigate('/demo')
    onClose()
  }

  const handleCtaClick = (plan) => {
    dispatch(
      fetchTrackEvent({
        eventName: 'upgrade_plan_option_clicked',
        eventType: ProductAnalyticsEventTypes.BUTTON_CLICK,
        distinctId: email,
        eventData: { planName: plan.key, source },
      }),
    )
    if (plan.key === 'professional') {
      setCheckoutLoading(true)
      dispatch(fetchCreateCheckoutSession({ customerEmail: email, source: source || 'upgrade_dialog' }))
    } else if (plan.key === 'hospital') {
      handleContactUs()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant='h6' fontWeight={700}>
          Unlock Full Access
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {isPendingApproval && (
          <Alert severity='info' sx={{ mb: 3 }}>
            Your account is pending verification. We are reviewing your organization and will notify you by email once approved. Paid plans will be available after your account is verified. This typically takes up to 48 hours.
          </Alert>
        )}

        {/* Caller-provided subtitles are contextual notices (e.g. free job limit reached), so emphasize them. */}
        <Typography
          variant='body2'
          color={subtitle ? 'warning.dark' : 'text.secondary'}
          fontWeight={subtitle ? 600 : 400}
          sx={{ mb: 3 }}
        >
          {subtitle || DEFAULT_SUBTITLE}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {PLAN_OPTIONS.map(plan => {
            const isDisabledByApproval = isPendingApproval && plan.key === 'professional'
            const isLoading = checkoutLoading && plan.key === 'professional'
            const ctaLabel = isDisabledByApproval ? 'Pending Approval' : plan.ctaLabel
            const ctaDisabled = isDisabledByApproval || isLoading || plan.ctaDisabled

            return (
              <Card
                key={plan.key}
                variant='outlined'
                sx={{
                  borderRadius: '12px',
                  borderColor: plan.isHighlighted ? theme.palette.primary.main : theme.palette.grey[200],
                  borderWidth: plan.isHighlighted ? 2 : 1,
                }}
              >
                <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Typography variant='h6' fontWeight={700} gutterBottom>
                    {plan.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 1 }}>
                    <Typography variant='h5' fontWeight={800}>
                      {plan.price}
                    </Typography>
                    {plan.priceSuffix && (
                      <Typography variant='body2' color='text.secondary'>
                        {plan.priceSuffix}
                      </Typography>
                    )}
                  </Box>
                  {plan.description && (
                    <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                      {plan.description}
                    </Typography>
                  )}
                  <Box component='ul' sx={{ pl: 2, mb: 2, mt: 0, flexGrow: 1 }}>
                    {plan.features.map(feature => (
                      <Typography
                        key={feature}
                        component='li'
                        variant='body2'
                        color='text.secondary'
                        sx={{ mb: 0.5 }}
                      >
                        {feature}
                      </Typography>
                    ))}
                  </Box>
                  <Button
                    variant={plan.ctaVariant}
                    fullWidth
                    disabled={ctaDisabled}
                    startIcon={isLoading ? <CircularProgress size={16} color='inherit' /> : null}
                    onClick={() => handleCtaClick(plan)}
                  >
                    {isLoading ? 'Redirecting to checkout...' : ctaLabel}
                  </Button>
                </CardContent>
              </Card>
            )
          })}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default UpgradePlanDialog
